/**
 * The case in the client's words (Lloyd, Aug 19 2026).
 *
 * *"I think we definitely need to see all of these steps on our side ... But, I don't know if we
 * need all of these steps to be visible to the client. In the client's portal, can we have a
 * simpler view for them to see?"*
 *
 * ## What the client actually sees today, which is not what he thinks
 *
 * The thirty-nine steps have never reached the client portal — it shows the raw internal status
 * string and a history list of the same. So a client reads *"court respond by date satisfied
 * (Pending)"* and *"citation received (Approving)"*: our database vocabulary, parenthesised
 * internal state and all. That is a worse problem than the one he asked about, and it is what this
 * file fixes.
 *
 * The one place all thirty-nine boxes really are shown to an outsider is the partner share link.
 *
 * ## Why eight, and why these words
 *
 * The eight canonical stages (CASE_STAGES) are already the only steps the system reliably knows, so
 * a client view built on them can never show a box that will not fill in. Everything finer is
 * internal process — the client does not benefit from knowing that a motion for continuance was an
 * option, and a dashed box on a client's screen invites a question staff then have to answer.
 *
 * The labels are written for somebody who is not a lawyer and is worried about a ticket. No
 * abbreviations, no "LOR", no internal state in brackets. `description` is the sentence under the
 * bar explaining what the stage means for them, which is the part that stops the "what does this
 * mean?" phone call.
 */

import { stageForStatus, CLOSED_STAGE_INDEX } from './CaseStatusTimeline';

export interface ClientStage {
  /** Fits in a bar segment. */
  short: string;
  /** Full name, shown as the current stage heading. */
  label: string;
  /** What it means for the client, in one sentence. */
  description: string;
}

/**
 * Index-for-index with CASE_STAGES. Never reorder one without the other — the index is the
 * shared language between them, and a mismatch would tell a client the wrong thing about their
 * own case.
 */
export const CLIENT_STAGES: ClientStage[] = [
  {
    short: 'Started',
    label: 'Getting started',
    description:
      'We have your citation and are getting your case ready. Nothing is needed from you yet.',
  },
  {
    short: 'Court told',
    label: 'We have told the court we represent you',
    description:
      'Your letter of representation is with the court. From here, the court contacts us rather than you.',
  },
  {
    short: 'Confirmed',
    label: 'The court has confirmed us',
    description:
      'The court has acknowledged that we act for you, and your case is now in their system as represented.',
  },
  {
    short: 'Court date',
    label: 'Your court date is set',
    description:
      'The court has given us a date. You do not need to attend unless we tell you otherwise.',
  },
  {
    short: 'Offer',
    label: 'We have an offer from the court',
    description:
      'The prosecutor has made an offer on your case. We will explain it and recommend what to do.',
  },
  {
    short: 'Answered',
    label: 'Your answer is with the court',
    description:
      'You have told us how you want to proceed and we have passed that on to the court.',
  },
  {
    short: 'Conditions',
    label: 'Completing the conditions',
    description:
      'There are things to finish — a course, a payment or paperwork. We will tell you what and by when.',
  },
  {
    short: 'Closed',
    label: 'Your case is closed',
    description: 'Everything is finished. Nothing further is needed from you.',
  },
];

/** Shown when a case has no status the eight stages recognise. Honest rather than blank. */
export const CLIENT_STAGE_UNKNOWN: ClientStage = {
  short: 'In progress',
  label: 'Your case is with us',
  description:
    'We are working on your case. Your next update will appear here as soon as there is one.',
};

/**
 * The client's stage for an internal status name.
 *
 * Delegates to `stageForStatus` on purpose rather than keeping a second table — two mappings of the
 * same eleven statuses would drift, and the direction it would drift is a client being told
 * something the case page disagrees with.
 */
export function clientStageForStatus(statusName: string | null | undefined): number {
  return stageForStatus(statusName);
}

/** The stage to describe, or null when nothing recognisable is set. */
export function clientStage(stageIndex: number): ClientStage | null {
  if (stageIndex < 0 || stageIndex >= CLIENT_STAGES.length) return null;
  return CLIENT_STAGES[stageIndex];
}

/**
 * An internal status name rewritten for a client, e.g. for a history list.
 *
 * Anything unrecognised returns null rather than falling back to the raw string. A status nobody
 * has written a client sentence for is exactly the kind of value that should not be shown to one.
 */
export function describeStatusForClient(statusName: string | null | undefined): string | null {
  const stage = clientStage(clientStageForStatus(statusName));
  return stage ? stage.label : null;
}

export function isClosedForClient(stageIndex: number): boolean {
  return stageIndex === CLOSED_STAGE_INDEX;
}
